import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import { useNavigate } from 'react-router-dom';
import { showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import HeaderInfo from '../../../../Components/Common/HeaderInfo';
import useASIItemMaster from '../../../../hooks/useASIItemMaster';
import Remarks from './Remarks';
import Remarks__list from './Remarks__list';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import { handleDeleteTableRow, filterRemarksList } from '../../../../Custom/Custom';

// API configuration exactly like BlockH - using dynamic token with multiple fallbacks
const getAuthConfig = () => {
  // Try multiple possible token keys
  let token = localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('userToken');

  if (!token) {
    // Try to get from sessionStorage as well
    token = sessionStorage.getItem('token') ||
      sessionStorage.getItem('jwt') ||
      sessionStorage.getItem('accessToken');
  }

  if (!token) {
    console.error('No token found in localStorage or sessionStorage');
    console.log('Available localStorage keys:', Object.keys(localStorage));
    console.log('Available sessionStorage keys:', Object.keys(sessionStorage));
    return null;
  }

  console.log('Token found:', token.substring(0, 20) + '...');

  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  };
};

function InputItemsIndigenous() {
  const navigate = useNavigate();
  const dtableRef = useRef(null);

  // Use the custom hook for ASI Item Master data
  const {
    itemMasterData,
    filteredItems,
    isLoading: loadingItemMaster,
    debouncedSearch,
    loadMoreData,
    hasMoreData,
    isLoadingMore,
    // fetchUnitByItemCode
  } = useASIItemMaster();

  // State for header data
  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  const [itemData, setItemData] = useState({});
  // const [checkedItems, setCheckedItems] = useState([]); // Commented out - using Action button for deletion
  const [buttonStatus, setButtonStatus] = useState('');
  const [originalValues, setOriginalValues] = useState({}); // Track original values for itemCode
  const [validationErrors, setValidationErrors] = useState({}); // Track validation errors

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });
  const [remarksList, setRemarksList] = useState([]);

  const [totalProfitandLossAmount, setTotalProfitandLossAmount] = useState({
    SL_No: '',
    Description: '',
    Amount: ''
  });

  // State for managing dropdown visibility for each row and field
  const [dropdownStates, setDropdownStates] = useState({});
  const [selectedIndex, setSelectedIndex] = useState({}); // Track selected index for keyboard navigation
  const dropdownItemRefs = useRef({}); // Refs for dropdown items
  const justOpenedRef = useRef({}); // Track if dropdown was just opened to prevent immediate toggle

  // Get already selected item codes from all rows (first 10 user editable rows)
  // This will be used to filter out already selected items from dropdown
  const selectedItemCodes = useMemo(() => {
    const selected = {};
    for (let i = 0; i < 10; i++) {
      const item = itemData[i];
      if (item && item.itemCode && item.itemCode.toString().trim() !== '') {
        selected[i] = item.itemCode.toString().trim();
      }
    }
    return selected;
  }, [itemData]);

  // Function to get filtered items for a specific row (excluding already selected items in other rows)
  const getFilteredItemsForRow = useCallback((rowIndex, items) => {
    return items.filter(dropdownItem => {
      const dropdownItemCode = dropdownItem.ASICC_CODE?.toString().trim();
      // Check if this item is selected in any other row (not the current row)
      for (const [index, code] of Object.entries(selectedItemCodes)) {
        if (parseInt(index) !== rowIndex && code === dropdownItemCode) {
          return false; // Item is selected in another row, filter it out
        }
      }
      return true;
    });
  }, [selectedItemCodes]);

  // Get selected Item Descriptions from table for Remarks Items dropdown
  // Include both: user selected items (first 10 rows) + fixed items (rows 11-24)
  const selectedItemDescriptions = useMemo(() => {
    const descriptions = [];

    // Add user selected items from first 10 rows
    for (let i = 0; i < 10; i++) {
      const item = itemData[i];
      if (item && item.itemDesc && item.itemDesc.toString().trim() !== '') {
        descriptions.push(item.itemDesc.toString().trim());
      }
    }

    // Add fixed items (rows 11-24) - these should always be shown
    const fixedItems = [
      'Other basic items(indigenous)',
      'Non-basic Chemicals-All kinds',
      'Packing items',
      'Electricity own generated',
      'Electricity purchased & consumed',
      'Petrol,Diesel,Oil,Lubricants',
      'Coal Consumed',
      'Gas consumed',
      'Other fuel consumed',
      'Consumable store',
      'Any additional requirement of electricity(unmet demand)'
    ];

    // Add fixed items that are not already in descriptions
    fixedItems.forEach(fixedItem => {
      if (!descriptions.includes(fixedItem)) {
        descriptions.push(fixedItem);
      }
    });

    return descriptions;
  }, [itemData]);

  // Function to toggle dropdown for specific row and field
  const toggleDropdown = (rowIndex, fieldType) => {
    const key = `${rowIndex}-${fieldType}`;
    setDropdownStates(prev => {
      const newState = {};
      // Close all other dropdowns and toggle current one
      const isCurrentlyOpen = prev[key] || false;
      newState[key] = !isCurrentlyOpen;
      return newState;
    });
    // Reset selected index when opening dropdown
    setSelectedIndex(prev => {
      const currentState = prev[key];
      if (currentState === undefined || currentState >= 0) {
        return { ...prev, [key]: -1 };
      }
      return prev;
    });
  };

  // Function to open dropdown (without toggle)
  const openDropdown = (rowIndex, fieldType) => {
    const key = `${rowIndex}-${fieldType}`;
    setDropdownStates(prev => {
      // If already open, don't do anything
      if (prev[key]) {
        return prev;
      }
      const newState = {};
      // Close all other dropdowns and open current one
      newState[key] = true;
      // Mark as just opened to prevent immediate toggle
      justOpenedRef.current[key] = true;
      // Clear the flag after a short delay
      setTimeout(() => {
        justOpenedRef.current[key] = false;
      }, 300);
      return newState;
    });
    // Reset selected index when opening dropdown
    setSelectedIndex(prev => ({ ...prev, [key]: -1 }));
  };

  // Function to close dropdown for specific row and field
  const closeDropdown = (rowIndex, fieldType) => {
    const key = `${rowIndex}-${fieldType}`;
    setDropdownStates(prev => ({
      ...prev,
      [key]: false
    }));
    // Reset selected index when closing dropdown
    setSelectedIndex(prev => ({ ...prev, [key]: -1 }));
  };

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setDropdownStates({});
    setSelectedIndex({});
  };



  const itemCodeArray = ['9920100', '9990100', '9920300', '9990800', '9990400'
    , '9990500', '9990600', '9990700', '9990900', '9920400', '9922000', '9992000', '9993000',
    '9999900'];



  // Handle search input change
  const handleSearchChange = (value) => {
    debouncedSearch(value);
  };



  // Function to fetch existing data from API
  const fetchExistingData = useCallback(async (dslNo, surveyYear) => {
    if (!dslNo || !surveyYear) {
      console.log('DSL No or Survey Year not available for fetching data');
      dataLoadedRef.current = true;
      return;
    }

    console.log('Starting API call for get-data with DSL:', dslNo, 'Year:', surveyYear);
    showLoading(true);
    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        console.error('Authentication token not found for fetching data');
        showLoading(false);
        return;
      }

      // Extract only year part (e.g., "2024" from "2024-25")
      const yearOnly = surveyYear ? surveyYear.split('-')[0] : surveyYear;

      const payload = {
        dsl_no: dslNo,
        survey_year: yearOnly
      };

      console.log('Fetching existing data with payload:', payload);

      const response = await axios.post(`${API_URL}api/input-items-indigenous/get-data`, payload, currentConfig);

      if (response.data && response.data.status && response.data.data) {
        console.log('Data fetched successfully:', response.data.data);

        // Map API data to form data
        const apiData = response.data.data;
        const mappedData = {};

        // Separate user data (first 10 rows) and system data (special items)
        const userData = [];
        const systemData = [];

        // Special item codes for system rows
        const specialItemCodes = ['9920100', '9990100', '9920300', '9990800', '9990400',
          '9990500', '9990600', '9990700', '9990900', '9920400',
          '9922000', '9992000', '9993000', '9999900'];

        apiData.forEach((item) => {
          // Convert item_code to string for comparison (API returns number)
          const itemCodeStr = item.item_code?.toString() || '';
          const isSystemItem = specialItemCodes.includes(itemCodeStr);

          if (isSystemItem) {
            systemData.push(item);
          } else {
            userData.push(item);
          }
        });

        // Map user data to first 10 rows (0-9)
        userData.forEach((item, index) => {
          if (index < 10) {
            mappedData[index] = {
              id: index,
              dslNo: item.dsl_no?.toString() || '',
              surveyYear: item.survey_year?.toString() || '',
              itemDesc: item.item_desc || '',
              itemCode: item.item_code?.toString() || '',
              unitOfQty: item.unit_of_qty || '',
              qtyConsumed: item.qty_consumed?.toString() || '',
              purchaseValue: item.purchase_value?.toString() || '',
              ratePerUnit: item.rate_per_unit?.toString() || '',
              statusFlag: item.status_flag || '',
              saveOrderBy: index + 1,
              itemCodeClass: 'textBox_back_color_white'
            };
          }
        });

        // Fill remaining user rows with empty data if less than 10 items
        for (let i = userData.length; i < 10; i++) {
          mappedData[i] = {
            id: i,
            dslNo: dslNo,
            surveyYear: surveyYear,
            itemDesc: '',
            itemCode: '',
            unitOfQty: '',
            qtyConsumed: '',
            purchaseValue: '',
            ratePerUnit: '',
            statusFlag: '',
            saveOrderBy: i + 1,
            itemCodeClass: 'textBox_back_color_white'
          };
        }

        specialItemCodes.forEach((code, index) => {
          const actualIndex = 10 + index;
          // Convert item_code to string for comparison
          const systemItem = systemData.find(item => item.item_code?.toString() === code);

          if (systemItem) {
            mappedData[actualIndex] = {
              id: actualIndex,
              dslNo: systemItem.dsl_no?.toString() || dslNo,
              surveyYear: systemItem.survey_year?.toString() || surveyYear,
              itemDesc: systemItem.item_desc || '',
              itemCode: systemItem.item_code?.toString() || '',
              unitOfQty: systemItem.unit_of_qty || '',
              qtyConsumed: systemItem.qty_consumed?.toString() || '',
              purchaseValue: systemItem.purchase_value?.toString() || '',
              ratePerUnit: systemItem.rate_per_unit?.toString() || '',
              statusFlag: systemItem.status_flag || '',
              saveOrderBy: actualIndex + 1,
              itemCodeClass: 'textBox_back_color_white'
            };
          } else {
            // Fallback to default special items if not found in API
            const defaultSpecialItems = [
              { itemDesc: 'Other basic items(indigenous)', itemCode: '9920100', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '1896774347', ratePerUnit: '0' },
              { itemDesc: 'Total Basic Items(1 to 11)', itemCode: '9990100', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '2442955149', ratePerUnit: '0' },
              { itemDesc: 'Non-basic Chemicals-All kinds', itemCode: '9920300', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Packing items', itemCode: '9990800', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Electricity own generated', itemCode: '9990400', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Electricity purchased & consumed', itemCode: '9990500', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Petrol,Diesel,Oil,Lubricants', itemCode: '9990600', unitOfQty: 'Litre', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Coal Consumed', itemCode: '9990700', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Gas consumed', itemCode: '9990900', unitOfQty: 'Cubic Meter', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Other fuel consumed', itemCode: '9920400', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Consumable store', itemCode: '9922000', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
              { itemDesc: 'Total Non-basic items(13 to 21)', itemCode: '9992000', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '0', ratePerUnit: '0' },
              { itemDesc: 'Total Input(12 + 22)', itemCode: '9993000', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '0', ratePerUnit: '0' },
              { itemDesc: 'Any additional requirement of electricity(unmet demand)', itemCode: '9999900', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' }
            ];

            const defaultItem = defaultSpecialItems[index];
            mappedData[actualIndex] = {
              id: actualIndex,
              dslNo: dslNo,
              surveyYear: surveyYear,
              itemDesc: defaultItem.itemDesc,
              itemCode: defaultItem.itemCode,
              unitOfQty: defaultItem.unitOfQty,
              qtyConsumed: defaultItem.qtyConsumed,
              purchaseValue: defaultItem.purchaseValue,
              ratePerUnit: defaultItem.ratePerUnit,
              statusFlag: '',
              saveOrderBy: actualIndex + 1,
              itemCodeClass: 'textBox_back_color_white'
            };
          }
        });

        setItemData(mappedData);

        // Map remarks data from API response (similar to FixedAssets.jsx)
        let mappedRemarksList = [];
        if (response.data.remarks && Array.isArray(response.data.remarks)) {
          mappedRemarksList = response.data.remarks.map((remark, index) => {
            // Extract block letter from "Block H" format (e.g., "Block H" -> "H")
            let blockLetter = '';
            if (remark.block) {
              blockLetter = remark.block.replace(/^Block\s+/i, '').trim();
            }

            return {
              slNo: index + 1,
              blocks: blockLetter || remark.block || '',
              columns: remark.columns_name || remark.columnsName || '',
              items: remark.items || '',
              remarks: remark.remarks || '',
              remarksCreatedOn: remark.created_date || remark.createdDate || '',
              remarksBy: remark.updated_by || remark.updatedBy || remark.created_by || '',
              role: remark.updated_by || remark.updatedBy || ''
            };
          });
          setRemarksList(mappedRemarksList);
        } else {
          setRemarksList([]);
        }

        // Set default remarksData
        setRemarksData({
          remarksBlock: (mappedRemarksList.length > 0) ? 'ALL' : 'SELECT_REMARKS',
          remarksItems: '',
          remarks: ''
        });

        // Map totalProfitandLossAmount from API response
        if (response.data.totalProfitandLossAmount) {
          const profitLossData = response.data.totalProfitandLossAmount;
          setTotalProfitandLossAmount({
            SL_No: profitLossData.SL_No !== undefined ? profitLossData.SL_No : (profitLossData.sl_No !== undefined ? profitLossData.sl_No : ''),
            Description: profitLossData.Description || profitLossData.description || '',
            Amount: profitLossData.Amount !== undefined && profitLossData.Amount !== null ? profitLossData.Amount.toString() : (profitLossData.amount !== undefined && profitLossData.amount !== null ? profitLossData.amount.toString() : '')
          });
        } else {
          // Reset to empty if not available
          setTotalProfitandLossAmount({
            SL_No: '',
            Description: '',
            Amount: ''
          });
        }

        dataLoadedRef.current = true; // Mark data as loaded
        console.log('API data loaded successfully, dataLoadedRef set to true');
        console.log('Remarks loaded:', mappedRemarksList);
        // toast.success('Data loaded successfully from server', messages.COLOR_RED);
      } else {
        console.log('No data found or invalid response');
        initializeTableData();
        // Reset totalProfitandLossAmount if no data
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
        dataLoadedRef.current = true; // Mark data as loaded even if no data found
        console.log('No data found, initialized with default data, dataLoadedRef set to true');
      }
    } catch (error) {
      console.error('Error fetching data from API:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 404) {
        console.log('No existing data found, initializing with default data');
        initializeTableData();
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
      } else {
        toast.error('Failed to load existing data. Using default form.', messages.COLOR_RED);
        initializeTableData();
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
      }
      dataLoadedRef.current = true; // Mark data as loaded even on error
      console.log('Error occurred, initialized with default data, dataLoadedRef set to true');
    } finally {
      showLoading(false);
    }
  }, []); // Empty dependency array since function doesn't depend on any props or state

  // Use ref to track if data has been loaded to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  // Memoize the fetch function to prevent recreation
  const fetchHeaderData = useCallback(async () => {
    // Prevent multiple API calls using refs
    if (dataLoadedRef.current || isLoadingRef.current || apiCallMadeRef.current) {
      console.log('Data already loaded, loading in progress, or API call already made - skipping');
      return;
    }

    try {
      const balanceSheetData = localStorage.getItem('balanceSheetFormData');

      if (balanceSheetData) {
        const parsedData = JSON.parse(balanceSheetData);

        const headerInfo = {
          despatchSlNo: parsedData.dslNo || '',
          permanentSlNo: parsedData.pslNo || '',
          companyName: parsedData.nameUnit || '',
          dslNo: parsedData.dslNo || '',
          surveyYear: parsedData.year || ''
        };

        setHeaderData(headerInfo);

        // Fetch existing data if DSL No and Survey Year are available
        if (headerInfo.dslNo && headerInfo.surveyYear) {
          apiCallMadeRef.current = true; // Mark API call as made
          isLoadingRef.current = true;
          await fetchExistingData(headerInfo.dslNo, headerInfo.surveyYear);
          isLoadingRef.current = false;
        } else {
          initializeTableData();
        }
      } else {
        console.log('No balanceSheet data found in localStorage');
        setHeaderData({
          despatchSlNo: 'DSL No. not set',
          permanentSlNo: 'PSL No. not set',
          companyName: 'Company name not set',
          dslNo: '',
          surveyYear: ''
        });
        initializeTableData();
      }
    } catch (error) {
      console.error('Error fetching data from localStorage:', error);
      setHeaderData({
        despatchSlNo: 'Error loading data',
        permanentSlNo: 'Error loading data',
        companyName: 'Error loading data',
        dslNo: '',
        surveyYear: ''
      });
      initializeTableData();
    }
  }, []);

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    fetchHeaderData();
  }, [fetchHeaderData]); // Only depend on the memoized function


  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedElement = event.target;

      // Check if click is inside any dropdown container
      const clickedDropdownContainer = clickedElement.closest('.position-relative');

      // Check if click is on dropdown menu items
      const isDropdownMenu = clickedElement.closest('.position-absolute.bg-white.border.rounded.shadow-sm');

      // Check if click is on an input field
      const isInputField = clickedElement.tagName === 'INPUT' && clickedElement.type === 'text';

      // Find all open dropdowns
      const openDropdowns = Object.keys(dropdownStates).filter(key => dropdownStates[key]);

      if (openDropdowns.length > 0) {
        // If click is on an input field
        if (isInputField) {
          // Check if the clicked input container has an open dropdown visible
          let isInsideOpenDropdown = false;

          if (clickedDropdownContainer) {
            // Check if this container has a visible dropdown menu
            const visibleDropdown = clickedDropdownContainer.querySelector('.position-absolute.bg-white.border.rounded.shadow-sm');
            if (visibleDropdown && visibleDropdown.offsetParent !== null) {
              // Dropdown is visible in this container, so don't close
              isInsideOpenDropdown = true;
            }
          }

          // If clicked input is not inside any open dropdown container, close all dropdowns
          if (!isInsideOpenDropdown) {
            closeAllDropdowns();
          }
        } else if (!clickedDropdownContainer && !isDropdownMenu) {
          // If click is completely outside any dropdown container and not on dropdown menu, close all
          closeAllDropdowns();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownStates]);




  // Initialize table with default data
  const initializeTableData = () => {
    const initialData = {};

    // First 10 rows (user editable)
    for (let i = 0; i < 10; i++) {
      initialData[i] = {
        id: i,
        dslNo: headerData.despatchSlNo,
        surveyYear: '2023-24',
        itemDesc: '',
        itemCode: '',
        unitOfQty: '',
        qtyConsumed: '',
        purchaseValue: '',
        ratePerUnit: '',
        statusFlag: '',
        saveOrderBy: 1,
        itemCodeClass: 'textBox_back_color_white'
      };
    }

    // Special items from 11-24 (system rows)
    const specialItems = [
      { itemDesc: 'Other basic items(indigenous)', itemCode: '9920100', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '1896774347', ratePerUnit: '0' },
      { itemDesc: 'Total Basic Items(1 to 11)', itemCode: '9990100', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '2442955149', ratePerUnit: '0' },
      { itemDesc: 'Non-basic Chemicals-All kinds', itemCode: '9920300', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Packing items', itemCode: '9990800', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Electricity own generated', itemCode: '9990400', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Electricity purchased & consumed', itemCode: '9990500', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Petrol,Diesel,Oil,Lubricants', itemCode: '9990600', unitOfQty: 'Litre', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Coal Consumed', itemCode: '9990700', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Gas consumed', itemCode: '9990900', unitOfQty: 'Cubic Meter', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Other fuel consumed', itemCode: '9920400', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Consumable store', itemCode: '9922000', unitOfQty: 'Tonne', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' },
      { itemDesc: 'Total Non-basic items(13 to 21)', itemCode: '9992000', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '0', ratePerUnit: '0' },
      { itemDesc: 'Total Input(12 + 22)', itemCode: '9993000', unitOfQty: 'X', qtyConsumed: '0', purchaseValue: '0', ratePerUnit: '0' },
      { itemDesc: 'Any additional requirement of electricity(unmet demand)', itemCode: '9999900', unitOfQty: 'KWH', qtyConsumed: '', purchaseValue: '', ratePerUnit: '' }
    ];

    // Add special items starting from index 10
    specialItems.forEach((item, index) => {
      const actualIndex = 10 + index;
      initialData[actualIndex] = {
        id: actualIndex,
        dslNo: headerData.despatchSlNo,
        surveyYear: '2023-24',
        itemDesc: item.itemDesc,
        itemCode: item.itemCode,
        unitOfQty: item.unitOfQty,
        qtyConsumed: item.qtyConsumed,
        purchaseValue: item.purchaseValue,
        ratePerUnit: item.ratePerUnit,
        statusFlag: '',
        saveOrderBy: actualIndex + 1,
        itemCodeClass: 'textBox_back_color_white'
      };
    });

    setItemData(initialData);
    dataLoadedRef.current = true; // Mark data as loaded
    console.log('Table data initialized, dataLoadedRef set to true');
  };

  // Industry standard validation functions
  const validateNumericInput = (value, fieldName, maxLength = null, allowDecimal = true, allowNegative = false) => {
    // Remove any non-numeric characters except decimal point and minus sign
    let sanitized = value;

    if (allowDecimal) {
      // Allow digits, single decimal point, and optionally minus sign
      sanitized = value.replace(/[^\d.-]/g, '');
      // Ensure only one decimal point
      const parts = sanitized.split('.');
      if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('');
      }
      // Ensure minus sign only at the start
      if (!allowNegative) {
        sanitized = sanitized.replace(/-/g, '');
      } else {
        sanitized = sanitized.replace(/^-?/, sanitized.startsWith('-') ? '-' : '');
        sanitized = sanitized.replace(/(?!^)-/g, '');
      }
    } else {
      // Only digits, optionally minus sign
      if (allowNegative) {
        sanitized = value.replace(/[^\d-]/g, '');
        sanitized = sanitized.replace(/^-?/, sanitized.startsWith('-') ? '-' : '');
        sanitized = sanitized.replace(/(?!^)-/g, '');
      } else {
        sanitized = value.replace(/[^\d]/g, '');
      }
    }

    // Apply max length if specified
    if (maxLength && sanitized.length > maxLength) {
      sanitized = sanitized.substring(0, maxLength);
    }

    return sanitized;
  };

  const validateItemCode = (value) => {
    // Item code should be exactly 7 digits
    const sanitized = value.replace(/[^\d]/g, '');
    return sanitized.substring(0, 7);
  };

  const validateRequired = (value, fieldName) => {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return null;
  };

  const validateNumberRange = (value, min = null, max = null) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return null; // Empty is allowed, will be checked by required validation
    }
    if (min !== null && numValue < min) {
      return `Value must be at least ${min}`;
    }
    if (max !== null && numValue > max) {
      return `Value must not exceed ${max}`;
    }
    return null;
  };

  const handleInputChange = (index, field, value) => {
    const newFormData = { ...itemData };
    const newErrors = { ...validationErrors };
    let sanitizedValue = value;
    let error = null;

    // Apply field-specific validation
    switch (field) {
      case 'itemCode':
        sanitizedValue = validateItemCode(value);
        if (sanitizedValue.length > 0 && sanitizedValue.length < 7) {
          error = 'Item Code must be 7 digits';
        }
        break;

      case 'qtyConsumed':
        sanitizedValue = validateNumericInput(value, 'Quantity Consumed', 18, true, false);
        if (sanitizedValue) {
          error = validateNumberRange(sanitizedValue, 0, null);
        }
        break;

      case 'purchaseValue':
        sanitizedValue = validateNumericInput(value, 'Purchase Value', 12, true, false);
        if (sanitizedValue) {
          error = validateNumberRange(sanitizedValue, 0, null);
        }
        break;

      case 'ratePerUnit':
        sanitizedValue = validateNumericInput(value, 'Rate Per Unit', 18, true, false);
        if (sanitizedValue) {
          error = validateNumberRange(sanitizedValue, 0, null);
        }
        break;

      case 'itemDesc':
        // Text field - allow alphanumeric and special characters, limit length
        sanitizedValue = value.substring(0, 200);
        break;

      case 'unitOfQty':
        // Text field - limit length
        sanitizedValue = value.substring(0, 19);
        break;

      default:
        sanitizedValue = value;
    }

    // Update form data
    newFormData[index][field] = sanitizedValue;
    setItemData(newFormData);

    // Update validation errors
    if (error) {
      newErrors[`${index}-${field}`] = error;
    } else {
      delete newErrors[`${index}-${field}`];
    }
    setValidationErrors(newErrors);
  };

  // Handle dropdown change for Item Description - using API data
  const handleItemDescriptionChange = (index, value) => {
    const newFormData = { ...itemData };

    if (value) {
      const selectedItem = itemMasterData.find(item => item.ITEM_NAME === value);
      if (selectedItem) {
        newFormData[index] = {
          ...newFormData[index],
          itemDesc: selectedItem.ITEM_NAME,
          itemCode: selectedItem.ASICC_CODE,
          unitOfQty: selectedItem.UNIT_NAME || ''
        };
      }
    } else {
      newFormData[index] = {
        ...newFormData[index],
        itemDesc: '',
        itemCode: '',
        unitOfQty: ''
      };
    }

    setItemData(newFormData);
    closeDropdown(index, 'itemDesc');
  };

  // Handle dropdown change for Item Code - using API data
  const handleItemCodeChange = async (index, value) => {
    const newFormData = { ...itemData };

    if (value) {
      const selectedItem = itemMasterData.find(item => item.ASICC_CODE === value);
      if (selectedItem) {
        newFormData[index] = {
          ...newFormData[index],
          itemDesc: selectedItem.ITEM_NAME,
          itemCode: selectedItem.ASICC_CODE,
          unitOfQty: selectedItem.UNIT_NAME || ''
        };
      }
    } else {
      newFormData[index] = {
        ...newFormData[index],
        itemDesc: '',
        itemCode: '',
        unitOfQty: ''
      };
    }

    setItemData(newFormData);
    closeDropdown(index, 'itemCode');
  };

  // Handle keyboard navigation in dropdown
  const handleKeyDown = (e, rowIndex, fieldType, filteredItemsList) => {
    const key = `${rowIndex}-${fieldType}`;
    const currentSelected = selectedIndex[key] !== undefined ? selectedIndex[key] : -1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Open dropdown if closed
        if (!dropdownStates[key]) {
          openDropdown(rowIndex, fieldType);
        }
        // Move selection down
        if (currentSelected < filteredItemsList.length - 1) {
          const newIndex = currentSelected + 1;
          setSelectedIndex(prev => ({ ...prev, [key]: newIndex }));
          // Scroll into view
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${key}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        // Open dropdown if closed
        if (!dropdownStates[key]) {
          openDropdown(rowIndex, fieldType);
        }
        // Move selection up
        if (currentSelected > 0) {
          const newIndex = currentSelected - 1;
          setSelectedIndex(prev => ({ ...prev, [key]: newIndex }));
          // Scroll into view
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${key}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (dropdownStates[key] && currentSelected >= 0 && currentSelected < filteredItemsList.length) {
          // Select the highlighted item
          if (fieldType === 'itemDesc') {
            handleItemDescriptionChange(rowIndex, filteredItemsList[currentSelected].ITEM_NAME);
          } else if (fieldType === 'itemCode') {
            handleItemCodeChange(rowIndex, filteredItemsList[currentSelected].ASICC_CODE);
          }
        } else if (!dropdownStates[key]) {
          // Open dropdown if closed
          openDropdown(rowIndex, fieldType);
        }
        break;

      case 'Escape':
        e.preventDefault();
        // Close dropdown
        closeDropdown(rowIndex, fieldType);
        break;

      case 'Tab':
        // Close dropdown on tab
        closeDropdown(rowIndex, fieldType);
        break;

      default:
        // For other keys, open dropdown if typing
        if (!dropdownStates[key] && e.key.length === 1) {
          openDropdown(rowIndex, fieldType);
        }
        break;
    }
  };

  // Function to handle focus event - store original value when user first focuses on itemCode field
  const handleOnFocus = (index, field) => {
    if (field === 'itemCode' && !originalValues[index]) {
      const currentValue = itemData[index] ? itemData[index].itemCode : '';
      console.log('Storing original value for index:', index, 'Value:', currentValue);
      setOriginalValues(prev => ({
        ...prev,
        [index]: currentValue
      }));
    }
  };

  // Function to handle blur event - make API call when itemCode changes
  const handleOnBlur = async (index, newValue) => {
    // Check if value has actually changed
    const originalValue = originalValues[index] || '';

    console.log('handleOnBlur - index:', index, 'Original Value:', originalValue, 'New Value:', newValue);

    // If value hasn't changed, don't make API call
    if (originalValue === newValue) {
      console.log('Value unchanged, skipping API call for index:', index);
      // Clear the original value tracking for this field
      setOriginalValues(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
      return false;
    }

    let newItemAdded = {
      "id": index,
      "dslNo": headerData.despatchSlNo,
      "surveyYear": "2023-24",
      "itemDesc": "",
      "itemCode": "",
      "unitOfQty": "",
      "qtyConsumed": "",
      "purchaseValue": "",
      "ratePerUnit": "",
      "statusFlag": "",
      "saveOrderBy": 1,
      "itemCodeClass": "textBox_back_color_white",
    };

    if (newValue === '') {
      newItemAdded.itemCode = "";
      handleTableRowData(index, newItemAdded);
      // Clear original value tracking
      setOriginalValues(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
      return false;
    }

    const asicc_code = newValue;
    const newObject = {
      asicc_code: asicc_code
    };

    if (asicc_code.length < 7) {
      newItemAdded.itemCode = "";
      handleTableRowData(index, newItemAdded);
      toast.error("Invalid NPCMS Code.", messages.COLOR_RED);
      // Clear original value tracking
      setOriginalValues(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
      return false;
    }

    showLoading(true);
    try {
      // Get fresh auth config exactly like BlockH
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      console.log('Making API call with config:', currentConfig);
      console.log('Request payload:', newObject);

      // Make API call exactly like BlockH
      const response = await axios.post(`${API_URL}api/asi/blockHFetchDataByItemCodeNPCMS`, newObject, currentConfig);
      const { data, status } = response.data;

      if (status) {
        const responseData = data.blockHData;
        newItemAdded = {
          "id": index,
          "dslNo": headerData.despatchSlNo,
          "surveyYear": "2023-24",
          "itemDesc": responseData.itemName,
          "itemCode": asicc_code,
          "unitOfQty": responseData.unitOfQuantity,
          "qtyConsumed": "",
          "purchaseValue": "",
          "ratePerUnit": "",
          "statusFlag": "",
          "saveOrderBy": 1,
          "itemCodeClass": "textBox_back_color_white",
        };
      } else {
        newItemAdded = {
          "id": index,
          "dslNo": headerData.despatchSlNo,
          "surveyYear": "2023-24",
          "itemDesc": '',
          "itemCode": '',
          "unitOfQty": '',
          "qtyConsumed": "",
          "purchaseValue": "",
          "ratePerUnit": "",
          "statusFlag": "",
          "saveOrderBy": 1,
          "itemCodeClass": "textBox_back_color_white",
        };
        toast.error('Item not present - Please check itemcode', messages.COLOR_RED);
      }
      handleTableRowData(index, newItemAdded);

    } catch (error) {
      console.error('API Error Details:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.', messages.COLOR_RED);
      } else {
        toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
      }
    } finally {
      showLoading(false);
      // Clear original value tracking in all exit points (success, error, early return)
      setOriginalValues(prev => {
        const newState = { ...prev };
        delete newState[index];
        return newState;
      });
    }
  };

  // Function to update table row data
  const handleTableRowData = (index, newItemAdded) => {
    setItemData(prevItemData => ({
      ...prevItemData,
      [index]: {
        ...prevItemData[index],
        ...newItemAdded
      }
    }));
    return true;
  };

  /* handleCheckboxChange function commented out - using Action button for deletion
  const handleCheckboxChange = (event, index) => {
    if (event.target.checked) {
      setCheckedItems([...checkedItems, index]);
    } else {
      setCheckedItems(checkedItems.filter((item) => item !== index));
    }
  };
  */

  // Function to delete a single row - uses reusable function from Custom.js
  const handleDeleteSingleRow = (index) => {
    handleDeleteTableRow(index, itemData, setItemData, 10, true);
  };

  const addRow = () => {
    const newIndex = Object.keys(itemData).length;
    const newItem = {
      id: newIndex,
      dslNo: headerData.despatchSlNo,
      surveyYear: '2023-24',
      itemDesc: '',
      itemCode: '',
      unitOfQty: '',
      qtyConsumed: '',
      purchaseValue: '',
      ratePerUnit: '',
      statusFlag: '',
      saveOrderBy: 1,
      itemCodeClass: 'textBox_back_color_white'
    };
    setItemData(prev => ({ ...prev, [newIndex]: newItem }));
  };

  /* DeleteRow function commented out - using Action button for deletion
  const DeleteRow = () => {
    if (checkedItems.length === 0) {
      toast.error('Please select at least one row for deletion.', messages.COLOR_RED);
      return false;
    }

    const newItemData = { ...itemData };
    checkedItems.forEach(index => {
      delete newItemData[index];
    });

    // Reindex the remaining items
    const reindexedData = {};
    Object.values(newItemData).forEach((item, newIndex) => {
      reindexedData[newIndex] = { ...item, id: newIndex };
    });

    setItemData(reindexedData);
    setCheckedItems([]);
  };
  */

  const handleReset = () => {
    const newItemData = { ...itemData };
    Object.keys(newItemData).forEach(key => {
      newItemData[key] = {
        ...newItemData[key],
        qtyConsumed: '',
        purchaseValue: '',
        ratePerUnit: ''
      };
    });
    setItemData(newItemData);
  };


  // Function to validate all form data before saving
  const validateFormData = () => {
    const errors = [];

    for (let i = 0; i < 10; i++) {
      const item = itemData[i];
      if (item && (item.itemDesc || item.itemCode || item.qtyConsumed || item.purchaseValue)) {
        // Validate Item Code if provided
        if (item.itemCode && item.itemCode.length > 0 && item.itemCode.length !== 7) {
          errors.push(`Row ${i + 1}: Item Code must be exactly 7 digits`);
        }

        // Validate Quantity Consumed
        if (item.qtyConsumed) {
          const qty = parseFloat(item.qtyConsumed);
          if (isNaN(qty) || qty < 0) {
            errors.push(`Row ${i + 1}: Quantity Consumed must be a positive number`);
          }
        }

        // Validate Purchase Value
        if (item.purchaseValue) {
          const value = parseFloat(item.purchaseValue);
          if (isNaN(value) || value < 0) {
            errors.push(`Row ${i + 1}: Purchase Value must be a positive number`);
          }
        }

        // If item code is provided, item description should also be provided
        if (item.itemCode && !item.itemDesc) {
          errors.push(`Row ${i + 1}: Item Description is required when Item Code is provided`);
        }
      }
    }

    return errors;
  };

  // Function to save data to API
  const handleSaveToAPI = async () => {
    if (!headerData.dslNo || !headerData.surveyYear) {
      toast.error('DSL No. and Survey Year are required to save data', messages.COLOR_RED);
      return false;
    }

    // Validate form data before saving
    const validationErrors = validateFormData();
    if (validationErrors.length > 0) {
      toast.error(`Please fix the following errors:\n${validationErrors.join('\n')}`, messages.COLOR_RED);
      return false;
    }

    showLoading(true);

    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Extract year only from survey year (e.g., "2024-25" -> "2024")
      const surveyYearOnly = headerData.surveyYear.split('-')[0] || '2024';

      // Prepare data for API - only first 10 rows (user editable)
      const dataToSave = [];

      for (let i = 0; i < 10; i++) {
        const item = itemData[i];
        if (item && (item.itemDesc || item.itemCode || item.qtyConsumed || item.purchaseValue)) {
          // Sanitize numeric values
          const qtyConsumed = item.qtyConsumed ? parseFloat(item.qtyConsumed) || 0 : 0;
          const purchaseValue = item.purchaseValue ? parseFloat(item.purchaseValue) || 0 : 0;

          // Validate numeric values are positive
          if (qtyConsumed < 0 || purchaseValue < 0) {
            toast.error(`Row ${i + 1}: Values cannot be negative`, messages.COLOR_RED);
            showLoading(false);
            return false;
          }

          const payload = {
            dsl_no: headerData.dslNo,
            survey_year: surveyYearOnly,
            item_code: (item.itemCode || '').toString().trim(),
            item_desc: (item.itemDesc || '').trim(),
            unit_of_qty: (item.unitOfQty || '').trim(),
            qty_consumed: qtyConsumed,
            purchase_value: purchaseValue
          };

          dataToSave.push(payload);
        }
      }

      if (dataToSave.length === 0) {
        toast.error('No data to save. Please fill at least one row.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Prepare remarks array
      const remarksArray = [];
      if (remarksData.remarksBlock || remarksData.remarksItems || remarksData.remarks) {
        remarksArray.push({
          dslNo: headerData.dslNo,
          surveyYear: surveyYearOnly,
          block: 'H',
          columnsName: remarksData.remarksItems || '',
          Items: remarksData.remarksBlock || '',
          remarks: remarksData.remarks || ''
        });
      }

      // Structure payload with data and remarks arrays
      const finalPayload = {
        data: dataToSave,
        remarks: remarksArray
      };

      console.log('Saving data to API:', finalPayload);

      const response = await axios.post(`${API_URL}api/input-items-indigenous/insert`, finalPayload, currentConfig);

      // Check response
      if (response.data && response.data.status) {
        toast.success(`Successfully saved ${dataToSave.length} items to database`, messages.COLOR_RED);

        // Mark page as saved for green header
        markPageAsSaved(PAGE_NAMES.INPUT_ITEMS_INDIGENOUS);
        // Refresh balance sheet validation to update header colors
        if (headerData.despatchSlNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.despatchSlNo, headerData.surveyYear);
        }

        return true;
      } else {
        toast.error('Failed to save data. Please check the data and try again.', messages.COLOR_RED);
        return false;
      }

    } catch (error) {
      console.error('API Error Details:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.', messages.COLOR_RED);
      } else {
        toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      showLoading(false);
    }
  };

  // Function to get proper row number display
  const getRowNumber = (index, totalRows) => {
    // For first 10 rows (1-10)
    if (index < 10) {
      return index + 1;
    }
    // For special rows (11-25) - these are fixed
    else if (index >= totalRows - 14) {
      const specialRowIndex = index - (totalRows - 14);
      return 11 + specialRowIndex;
    }
    // For dynamic rows added by user
    else {
      return index + 1;
    }
  };

  /* Function to check if row should have checkbox - commented out
  const shouldShowCheckbox = (index, totalRows) => {
    // Only first 10 rows and any additional user-added rows should have checkboxes
    return index < 10;
  };
  */

  // Function to check if row is a special/system row
  const isSpecialRow = (index, totalRows) => {
    return index >= totalRows - 14;
  };

  return (
    <>
      <div className="container-fluid py-3">
        {/* Header Section */}
        <HeaderInfo
          title="Input Items - indigenous items consumed"
          icon="fa fa-cubes"
          iconColor="#1976d2"
          titleColor="#1976d2"
          backgroundColor=""
          dispatchSlNo={headerData.despatchSlNo}
          permanentSlNo={headerData.permanentSlNo}
          companyName={headerData.companyName}
          companyPlaceholder="Enter Company Name"
          showBlockCType={false}
          includeTableStyles={false}
        />

        {/* Main Content Area */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                {/* <h6 className="fw-semibold">
                  DSL No: <span className="fw-normal me-5">{headerData.despatchSlNo}</span>
                  Survey Year: <span className="fw-normal">2023-24</span>
                </h6> */}
                <div className="table-responsive asi_block_table">
                  <table cellPadding="0" cellSpacing="0" className="table table-bordered block_c_table" id="velblockh" ref={dtableRef}>
                    <thead>
                      <tr>
                        <th>Sl. No.</th>
                        <th>Item Description</th>
                        <th>Item Code</th>
                        <th>Unit of Quantity</th>
                        <th>Quantity Consumed</th>
                        <th>Purchase Value (in Rs.)</th>
                        <th>Values from PL Account for information</th>
                        <th>Action</th>
                      </tr>
                      <tr>
                        <th style={{ textAlign: 'center' }}>(1)</th>
                        <th style={{ textAlign: 'center' }}>(2)</th>
                        <th style={{ textAlign: 'center' }}>(3)</th>
                        <th style={{ textAlign: 'center' }}>(4)</th>
                        <th style={{ textAlign: 'center' }}>(5)</th>
                        <th style={{ textAlign: 'center' }}>(6)</th>
                        <th style={{ textAlign: 'center' }}>(7)</th>
                        <th style={{ textAlign: 'center' }}>(8)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(itemData).map((item, i) => {
                        const totalRows = Object.keys(itemData).length;
                        let rowNumber;
                        let isIndex = false;
                        let isDisable = false;

                        // Calculate proper row number
                        if (i < 10) {
                          rowNumber = i + 1; // 1, 2, 3... 10
                        } else if (i >= totalRows - 14) {
                          const specialRowIndex = i - (totalRows - 14);
                          rowNumber = 11 + specialRowIndex; // 11, 12, 13... 25
                        } else {
                          rowNumber = i + 1; // Dynamic rows
                        }

                        // Hide row 12, 22 and 23 (these correspond to specific indices)
                        if (rowNumber === 12 || rowNumber === 22 || rowNumber === 23) {
                          return null;
                        }

                        if (i >= totalRows - 14) {
                          isIndex = true;
                        }

                        if (itemCodeArray.includes(itemData[item.id]?.itemCode) &&
                          i >= totalRows - 14) {
                          isDisable = true;
                        }

                        return (
                          <tr key={i}>
                            <td>
                              {rowNumber}
                              {/* Checkbox commented out - using Action button for deletion
                              {i < 10 && !isIndex &&
                                <>
                                  -
                                  <input
                                    type="checkbox"
                                    id={`checkbox-${i}-id`}
                                    checked={checkedItems.includes(i)}
                                    onChange={(event) => handleCheckboxChange(event, i)}
                                  />
                                </>
                              }
                              */}
                            </td>
                            <td>
                              {i < 10 ? (
                                // Searchable dropdown for first 10 rows
                                <div className="position-relative">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={itemData[item.id]?.itemDesc || ''}
                                    placeholder="Item Description"
                                    style={{ textAlign: 'center', paddingRight: '30px' }}
                                    onChange={(e) => {
                                      handleInputChange(item.id, 'itemDesc', e.target.value);
                                      handleSearchChange(e.target.value);
                                      // Reset selected index when typing
                                      setSelectedIndex(prev => ({ ...prev, [`${item.id}-itemDesc`]: -1 }));
                                    }}
                                    onFocus={(e) => {
                                      // Open dropdown on focus if it's closed (for keyboard navigation)
                                      // Use setTimeout to avoid conflict with mousedown
                                      setTimeout(() => {
                                        if (!dropdownStates[`${item.id}-itemDesc`]) {
                                          openDropdown(item.id, 'itemDesc');
                                        }
                                      }, 0);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const key = `${item.id}-itemDesc`;
                                      // Only toggle if dropdown wasn't just opened by mousedown
                                      if (!justOpenedRef.current[key]) {
                                        toggleDropdown(item.id, 'itemDesc');
                                      } else {
                                        // Clear the flag since we handled it
                                        justOpenedRef.current[key] = false;
                                      }
                                    }}
                                    onMouseDown={(e) => {
                                      // Open dropdown immediately on mousedown to ensure it opens on first click
                                      // This prevents the need for double-click
                                      if (!dropdownStates[`${item.id}-itemDesc`]) {
                                        openDropdown(item.id, 'itemDesc');
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      handleKeyDown(e, item.id, 'itemDesc', getFilteredItemsForRow(item.id, filteredItems));
                                    }}
                                  />
                                  <i className="fa fa-chevron-down position-absolute"
                                    style={{
                                      right: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      color: '#666',
                                      pointerEvents: 'none'
                                    }}></i>
                                  {dropdownStates[`${item.id}-itemDesc`] && (() => {
                                    // Filter out already selected items for this row
                                    const availableItems = getFilteredItemsForRow(item.id, filteredItems);
                                    return (
                                      <div
                                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                                        style={{
                                          top: '100%',
                                          left: 0,
                                          zIndex: 1000,
                                          maxHeight: '200px',
                                          overflowY: 'auto'
                                        }}
                                      >
                                        {availableItems.length > 0 ? (
                                          <>
                                            {availableItems.map((singleItem, index) => {
                                              const key = `${item.id}-itemDesc`;
                                              const isSelected = selectedIndex[key] === index;
                                              return (
                                                <div
                                                  key={index}
                                                  ref={(el) => {
                                                    if (el) {
                                                      dropdownItemRefs.current[`${key}-${index}`] = el;
                                                    }
                                                  }}
                                                  className="px-3 py-2 cursor-pointer hover-bg-light"
                                                  style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                                    outline: isSelected ? '2px solid #1976d2' : 'none',
                                                    outlineOffset: '-2px'
                                                  }}
                                                  onClick={() => {
                                                    handleItemDescriptionChange(item.id, singleItem.ITEM_NAME);
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#f8f9fa';
                                                    setSelectedIndex(prev => ({ ...prev, [key]: index }));
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                      e.target.style.backgroundColor = 'white';
                                                    }
                                                  }}
                                                  tabIndex={-1}
                                                >
                                                  {singleItem.ITEM_NAME}
                                                </div>
                                              );
                                            })}
                                            {hasMoreData && (
                                              <div
                                                className="px-3 py-2 text-center text-muted"
                                                onClick={loadMoreData}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                {isLoadingMore ? 'Loading more...' : 'Load more items'}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="px-3 py-2 text-muted">
                                            {loadingItemMaster ? 'Loading items...' : 'No items found'}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : (
                                // Disabled input for special rows (11-24)
                                <input
                                  type="text"
                                  className={`form-control ${['9990100', '9992000', '9993000'].includes(itemData[item.id]?.itemCode) ? 'text-bold-inside-textbox' : ''}`}
                                  value={itemData[item.id]?.itemDesc || ''}
                                  id={`itemDesc${i}`}
                                  name={`itemDesc${i}`}
                                  disabled
                                />
                              )}
                            </td>
                            <td className={`${itemData[item.id]?.itemCodeClass || ''}`}>
                              {i < 10 ? (
                                // Searchable dropdown for first 10 rows
                                <div className="position-relative">
                                  <input
                                    type="text"
                                    className={`form-control ${validationErrors[`${item.id}-itemCode`] ? 'is-invalid' : ''}`}
                                    value={itemData[item.id]?.itemCode || ''}
                                    placeholder="Item Code (7 digits)"
                                    style={{ textAlign: 'center', paddingRight: '30px' }}
                                    onChange={(e) => {
                                      handleInputChange(item.id, 'itemCode', e.target.value);
                                      handleSearchChange(e.target.value);
                                      // Reset selected index when typing
                                      setSelectedIndex(prev => ({ ...prev, [`${item.id}-itemCode`]: -1 }));
                                    }}
                                    onFocus={(e) => {
                                      // Open dropdown on focus if it's closed (for keyboard navigation)
                                      // Use setTimeout to avoid conflict with mousedown
                                      setTimeout(() => {
                                        if (!dropdownStates[`${item.id}-itemCode`]) {
                                          openDropdown(item.id, 'itemCode');
                                        }
                                      }, 0);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const key = `${item.id}-itemCode`;
                                      // Only toggle if dropdown wasn't just opened by mousedown
                                      if (!justOpenedRef.current[key]) {
                                        toggleDropdown(item.id, 'itemCode');
                                      } else {
                                        // Clear the flag since we handled it
                                        justOpenedRef.current[key] = false;
                                      }
                                    }}
                                    onMouseDown={(e) => {
                                      // Open dropdown immediately on mousedown to ensure it opens on first click
                                      // This prevents the need for double-click
                                      if (!dropdownStates[`${item.id}-itemCode`]) {
                                        openDropdown(item.id, 'itemCode');
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      handleKeyDown(e, item.id, 'itemCode', getFilteredItemsForRow(item.id, filteredItems));
                                    }}
                                    onBlur={(e) => {
                                      const value = e.target.value;
                                      if (value && value.length > 0 && value.length < 7) {
                                        toast.warning('Item Code must be exactly 7 digits', messages.COLOR_RED);
                                      }
                                    }}
                                  />
                                  {validationErrors[`${item.id}-itemCode`] && (
                                    <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem', position: 'absolute', zIndex: 1001 }}>
                                      {validationErrors[`${item.id}-itemCode`]}
                                    </div>
                                  )}
                                  <i className="fa fa-chevron-down position-absolute"
                                    style={{
                                      right: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      color: '#666',
                                      pointerEvents: 'none'
                                    }}></i>
                                  {dropdownStates[`${item.id}-itemCode`] && (() => {
                                    // Filter out already selected items for this row
                                    const availableItems = getFilteredItemsForRow(item.id, filteredItems);
                                    return (
                                      <div
                                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                                        style={{
                                          top: '100%',
                                          left: 0,
                                          zIndex: 1000,
                                          maxHeight: '200px',
                                          overflowY: 'auto'
                                        }}
                                      >
                                        {availableItems.length > 0 ? (
                                          <>
                                            {availableItems.map((singleItem, index) => {
                                              const key = `${item.id}-itemCode`;
                                              const isSelected = selectedIndex[key] === index;
                                              return (
                                                <div
                                                  key={index}
                                                  ref={(el) => {
                                                    if (el) {
                                                      dropdownItemRefs.current[`${key}-${index}`] = el;
                                                    }
                                                  }}
                                                  className="px-3 py-2 cursor-pointer hover-bg-light"
                                                  style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                                    outline: isSelected ? '2px solid #1976d2' : 'none',
                                                    outlineOffset: '-2px'
                                                  }}
                                                  onClick={() => {
                                                    handleItemCodeChange(item.id, singleItem.ASICC_CODE);
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#f8f9fa';
                                                    setSelectedIndex(prev => ({ ...prev, [key]: index }));
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                      e.target.style.backgroundColor = 'white';
                                                    }
                                                  }}
                                                  tabIndex={-1}
                                                >
                                                  {singleItem.ASICC_CODE}
                                                </div>
                                              );
                                            })}
                                            {hasMoreData && (
                                              <div
                                                className="px-3 py-2 text-center text-muted"
                                                onClick={loadMoreData}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                {isLoadingMore ? 'Loading more...' : 'Load more items'}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="px-3 py-2 text-muted">
                                            {loadingItemMaster ? 'Loading items...' : 'No items found'}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : (
                                // Disabled input for special rows (11-24)
                                <input
                                  type="text"
                                  className="form-control"
                                  value={itemData[item.id]?.itemCode || ''}
                                  id={`itemCode${i}`}
                                  name={`itemCode${i}`}
                                  disabled={isDisable}
                                  maxLength="7"
                                  onChange={e => handleInputChange(item.id, 'itemCode', e.target.value)}
                                  onFocus={() => handleOnFocus(item.id, 'itemCode')}
                                  onBlur={(e) => handleOnBlur(item.id, e.target.value)}
                                />
                              )}
                            </td>
                            <td>
                              {['9990400', '9990500', '9999900'].includes(itemData[item.id]?.itemCode) ?
                                "KWH" :
                                <input
                                  type="text"
                                  className="form-control"
                                  value={itemData[item.id]?.unitOfQty || ''}
                                  id={`unitOfQty${i}`}
                                  name={`unitOfQty${i}`}
                                  maxLength="19"
                                  disabled
                                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                />
                              }
                            </td>
                            <td>
                              <div className="position-relative">
                                <input
                                  type="text"
                                  className={`form-control ${validationErrors[`${item.id}-qtyConsumed`] ? 'is-invalid' : ''}`}
                                  value={itemData[item.id]?.qtyConsumed || ''}
                                  id={`qtyConsumed${i}`}
                                  name={`qtyConsumed${i}`}
                                  maxLength="18"
                                  onChange={e => handleInputChange(item.id, 'qtyConsumed', e.target.value)}
                                  onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value && parseFloat(value) < 0) {
                                      handleInputChange(item.id, 'qtyConsumed', '0');
                                      toast.warning('Quantity cannot be negative', messages.COLOR_RED);
                                    }
                                  }}
                                  disabled={['9920100', '9920300', '9922000', '9990100'
                                    , '9990400', '9990600', '9990800', '9992000', '9993000'].includes(itemData[item.id]?.itemCode) && i !== 14 || i === 19}
                                />
                                {validationErrors[`${item.id}-qtyConsumed`] && (
                                  <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem' }}>
                                    {validationErrors[`${item.id}-qtyConsumed`]}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="position-relative">
                                <input
                                  type="text"
                                  className={`form-control ${validationErrors[`${item.id}-purchaseValue`] ? 'is-invalid' : ''}`}
                                  value={itemData[item.id]?.purchaseValue || ''}
                                  id={`purchaseValue${i}`}
                                  name={`purchaseValue${i}`}
                                  maxLength="12"
                                  onChange={e => handleInputChange(item.id, 'purchaseValue', e.target.value)}
                                  onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value && parseFloat(value) < 0) {
                                      handleInputChange(item.id, 'purchaseValue', '0');
                                      toast.warning('Purchase Value cannot be negative', messages.COLOR_RED);
                                    }
                                  }}
                                  disabled={['9990400', '9999900'].includes(itemData[item.id]?.itemCode) || (i >= 13 && i <= 23)}
                                />
                                {validationErrors[`${item.id}-purchaseValue`] && (
                                  <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem' }}>
                                    {validationErrors[`${item.id}-purchaseValue`]}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={itemData[item.id]?.ratePerUnit || ''}
                                id={`ratePerUnit${i}`}
                                name={`ratePerUnit${i}`}
                                maxLength="18"
                                disabled
                                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                              />
                            </td>
                            <td>
                              {i < 10 && (
                                <a
                                  href="#"
                                  className='d-block text-center'
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleDeleteSingleRow(i);
                                  }}
                                  title={`Delete Row ${i + 1}`}
                                >
                                  <i className="fa fa-trash"></i>
                                </a>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='card mb-3'>
          <div className='card-header'><h5 className='mb-0 pb-1 pt-1' style={{ fontWeight: 'bold' }}>Control Figures from Profit and Loss</h5></div>
          <div className='card-body'>
            <table className='table table-bordered' style={{ minWidth: '100%' }}>
              <thead>
                <tr>
                  <th>SL. No.</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {totalProfitandLossAmount.SL_No !== '' || totalProfitandLossAmount.Description !== '' || totalProfitandLossAmount.Amount !== '' ? (
                  <tr>
                    <td>{totalProfitandLossAmount.SL_No !== '' ? totalProfitandLossAmount.SL_No : ''}</td>
                    <td>{totalProfitandLossAmount.Description || ''}</td>
                    <td>{totalProfitandLossAmount.Amount !== '' ? totalProfitandLossAmount.Amount : ''}</td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center' }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>
        {/* Remarks Section */}
        <div className='row'>
          <div className='col-md-6 remarks-section'>

            <Remarks
              remarksData={remarksData}
              setRemarksData={setRemarksData}
              blockItemData={[]}
              filteredBlockItems={[]}
              isLoadingBlockItems={false}
              debouncedBlockItemSearch={() => { }}
              loadMoreBlockItemData={() => { }}
              hasMoreBlockItems={false}
              isLoadingMoreBlockItems={false}
              selectedItemsFromTable={selectedItemDescriptions}
              remarksList={remarksList}
              availableColumns={[5, 6]}
            />
          </div>
          <div className='col-md-6'>
            <div className='card'>
              <div className='card-body'>
                <h6 className='mb-3' style={{ fontWeight: 'bold', color: '#f56565' }}><i className='fa fa-exclamation-triangle me-2'></i>Note</h6>
                <ul className='list-styled' style={{ fontSize: '14px', color: '#f56565' }}>
                  <li>Col-4 and 5 are Mandatory</li>
                  <li>For OTHERS item, Discription and Code Col-3 are also Mandatory</li>
                </ul>
              </div>
            </div>
          </div>


        </div>
        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-lg-8 text-center text-lg-start mb-3 mb-lg-0">
            <button
              type="button"
              className="btn btn_orange me-1"
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange me-1"
              onClick={handleSaveToAPI}
            >
              Save
            </button>
            <button
              type="reset"
              className="btn btn_orange me-1"
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn_orange me-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success me-1 px-3"
              onClick={addRow}
            >
              <i className="fa fa-plus-circle me-1"></i>
              Add Row
            </button>
            {/* Delete Row button commented out - using Action button for deletion
            <button
              type="button"
              className="btn btn_red px-3"
              onClick={DeleteRow}
            >
              <i className="fa fa-minus-circle me-1"></i>
              Delete Row
            </button>
            */}
          </div>
          <div className="col-lg-4 text-center text-lg-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/employmentAndLabourCost')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={async () => {
                // Save data first, then navigate only if successful
                const saveSuccess = await handleSaveToAPI();
                if (saveSuccess) {
                  navigate('/input-items-imported');
                }
                // If save fails, stay on current page
              }}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
        <div className=''>
          <Remarks__list remarksData={useMemo(() => {
            return filterRemarksList(remarksList, remarksData);
          }, [remarksList, remarksData])} />
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
    </>
  );
}

export default InputItemsIndigenous; 